import React, { useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CATEGORY_ICONS } from "@/lib/categories";
import { useServiceI18n } from "@/lib/serviceI18n";
import { Label } from "@/components/CategoryCard";
import { Search, Sparkles, Bot, ArrowRight, Send, Compass } from "lucide-react";

const SUGGESTION_PROMPTS = {
  nl: ["ZZP'er, BTW snap ik niet", "Cyberaanval — hulp nodig", "Merk relaunchen", "Verzekering voor mijn webshop"],
  en: ["Freelancer, lost on VAT", "Cyber attack — need help", "Relaunch my brand", "Insurance for my webshop"],
  de: ["Selbstständig, MwSt. unklar", "Cyberangriff — Hilfe", "Marke neu launchen", "Versicherung für Webshop"],
  fr: ["Indépendant, perdu avec la TVA", "Cyberattaque — aide", "Relancer ma marque", "Assurance webshop"],
  es: ["Autónomo, perdido con IVA", "Ciberataque — ayuda", "Relanzar marca", "Seguro para mi e-commerce"],
  it: ["Autonomo, perso con IVA", "Cyberattacco — aiuto", "Rilanciare il brand", "Assicurazione e-commerce"],
  pt: ["Autônomo, perdido com impostos", "Ataque cibernético — ajuda", "Relançar marca", "Seguro para e-commerce"],
  pl: ["JDG, zagubiony w VAT", "Cyberatak — pomocy", "Relaunch marki", "Ubezpieczenie sklepu"],
  tr: ["Serbest çalışan, KDV", "Siber saldırı — yardım", "Marka yenileme", "Web mağazası sigortası"],
};

export const HeroSearch = () => {
  const { t, i18n } = useTranslation();
  const { catName, subName, subDiensten } = useServiceI18n();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);
  const [query, setQuery] = useState("");
  const [aiQuery, setAiQuery] = useState("");
  const [mode, setMode] = useState("ai");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const lang = (i18n.language || "nl").slice(0, 2).toLowerCase();
  const samples = SUGGESTION_PROMPTS[lang] || SUGGESTION_PROMPTS.en;

  useEffect(() => {
    let cancelled = false;
    const fetchCats = async (retries = 2) => {
      try {
        const r = await api.get("/service-categories");
        if (!cancelled) { setCategories(r.data); setLoadingCats(false); }
      } catch (err) {
        if (retries > 0 && !cancelled) {
          setTimeout(() => fetchCats(retries - 1), 800);
        } else if (!cancelled) {
          setLoadingCats(false);
        }
      }
    };
    fetchCats();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return categories;
    return categories
      .map((cat) => ({
        ...cat,
        subcategories: cat.subcategories.filter((s) =>
          subName(s).toLowerCase().includes(q) ||
          subDiensten(s).toLowerCase().includes(q) ||
          s.naam.toLowerCase().includes(q) ||
          s.diensten.toLowerCase().includes(q)
        ),
      }))
      .filter((cat) => cat.subcategories.length > 0 || catName(cat).toLowerCase().includes(q) || cat.naam.toLowerCase().includes(q));
  }, [categories, query, catName, subName, subDiensten]);

  const totalServices = categories.reduce((acc, c) => acc + c.subcategories.length, 0);

  const onPickDienst = (id) => { setOpen(false); navigate(`/onboarding?diensten=${encodeURIComponent(id)}`); };
  const onPickCategory = (categoryId) => { setOpen(false); navigate(`/diensten/${categoryId}`); };

  const onSearch = (e) => {
    e.preventDefault();
    if (filtered.length === 1 && filtered[0].subcategories.length === 1) {
      onPickDienst(filtered[0].subcategories[0].id);
      return;
    }
    navigate(`/onboarding`);
  };

  const onAiSubmit = (e) => {
    e.preventDefault();
    const q = aiQuery.trim();
    if (!q) { navigate("/sem"); return; }
    navigate(`/sem?q=${encodeURIComponent(q)}`);
  };

  return (
    <div ref={wrapRef} className="relative w-full">
      {/* Mode tabs — minimal */}
      <div className="flex items-center gap-1 mb-3" data-testid="hero-mode-tabs">
        <button
          type="button"
          onClick={() => { setMode("ai"); setOpen(false); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-[12px] font-semibold tracking-tight transition-all ${
            mode === "ai"
              ? "bg-[#0A1128] text-white"
              : "bg-transparent text-[#0A1128]/55 hover:text-[#0A1128] hover:bg-[#F3F0EA]"
          }`}
          data-testid="hero-mode-ai"
        >
          <Sparkles size={13} className={mode === "ai" ? "text-[#F5A623]" : ""} />
          {t("home.hero.mode_ai")}
          <span className="text-[9px] font-extrabold px-1.5 py-0.5 rounded-sm bg-[#F5A623] text-[#0A1128] uppercase tracking-[0.1em]">AI</span>
        </button>
        <button
          type="button"
          onClick={() => setMode("search")}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-[12px] font-semibold tracking-tight transition-all ${
            mode === "search"
              ? "bg-[#0A1128] text-white"
              : "bg-transparent text-[#0A1128]/55 hover:text-[#0A1128] hover:bg-[#F3F0EA]"
          }`}
          data-testid="hero-mode-search"
        >
          <Compass size={13} /> {t("home.hero.mode_search")}
        </button>
      </div>

      {mode === "ai" ? (
        <>
          <form
            onSubmit={onAiSubmit}
            className="relative bg-white border border-[#0A1128]/10 rounded-md shadow-[0_8px_32px_rgba(10,17,40,0.06)] focus-within:border-[#0A1128]/30 focus-within:shadow-[0_16px_48px_rgba(10,17,40,0.1)] transition-all"
            data-testid="hero-ai-form"
          >
            <div className="flex items-start gap-3 p-3 md:p-4">
              <div className="relative flex-shrink-0 mt-1">
                <div className="w-10 h-10 rounded-md bg-[#0A1128] text-white flex items-center justify-center font-heading font-bold text-[11px] tracking-wider">
                  SEM
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#16A34A] ring-2 ring-white bc-live-dot" />
              </div>
              <textarea
                placeholder={t("home.hero.ai_placeholder")}
                className="flex-1 bg-transparent outline-none text-[15px] md:text-[16px] leading-relaxed py-1.5 resize-none placeholder:text-[#0A1128]/40"
                value={aiQuery}
                onChange={(e) => setAiQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onAiSubmit(e); } }}
                rows={2}
                data-testid="hero-ai-input"
                autoComplete="off"
              />
              <button
                type="submit"
                className="btn-amber !rounded-md whitespace-nowrap !px-4 !py-2.5 flex-shrink-0 mt-1 group"
                data-testid="hero-ai-submit"
              >
                <Send size={14} className="transition-transform group-hover:translate-x-0.5" />
                <span className="hidden sm:inline">{t("home.hero.ai_btn")}</span>
              </button>
            </div>
          </form>

          {/* Prompt chips */}
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="eyebrow self-center hidden md:inline-block mr-1">
              {t("hero_search.try", "Probeer")}
            </span>
            {samples.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => navigate(`/sem?q=${encodeURIComponent(s)}`)}
                className="text-[12px] px-3 py-1.5 rounded-md border border-[#EAE6DF] bg-white hover:border-[#0A1128]/30 hover:bg-[#F3F0EA] text-[#0A1128]/75 hover:text-[#0A1128] transition-all"
                data-testid="hero-sample-prompt"
              >
                {s}
              </button>
            ))}
          </div>
        </>
      ) : (
        <form
          onSubmit={onSearch}
          className="bg-white border border-[#0A1128]/10 rounded-md p-3 md:p-4 flex flex-col md:flex-row gap-2 shadow-[0_8px_32px_rgba(10,17,40,0.06)]"
          data-testid="hero-search-form"
        >
          <div className="flex items-center gap-3 px-2 flex-1 relative">
            <Search size={18} className="text-[#0A1128]/40 flex-shrink-0" />
            <input
              type="text"
              placeholder={t("home.hero.search_spec")}
              className="w-full bg-transparent outline-none text-[15px] py-2 placeholder:text-[#0A1128]/40"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              data-testid="hero-search-spec"
              autoComplete="off"
            />
            <span className="text-[11px] text-[#0A1128]/40 hidden md:inline tabular">
              {totalServices} {t("services.count_suffix")}
            </span>
          </div>
          <button type="submit" className="btn-amber !rounded-md whitespace-nowrap !px-5" data-testid="hero-search-submit">
            <Search size={16} /> {t("home.hero.search_btn")}
          </button>
        </form>
      )}

      {/* Search dropdown */}
      {open && mode === "search" && (
        <div
          className="absolute left-0 right-0 mt-2 bg-white border border-[#EAE6DF] rounded-md shadow-[0_24px_64px_rgba(10,17,40,0.2)] z-[60] overflow-hidden max-h-[480px] overflow-y-auto"
          data-testid="hero-search-dropdown"
        >
          <div className="px-5 py-3 bg-[#F3F0EA]/40 border-b border-[#EAE6DF] flex items-center justify-between">
            <span className="eyebrow">
              {query ? t("hero_search.results", "Resultaten") : t("hero_search.all_services", "Alle diensten")}
            </span>
            <span className="text-xs text-[#0A1128]/50 tabular">
              {totalServices} {t("services.count_suffix")}
            </span>
          </div>

          {loadingCats ? (
            <div className="p-8 text-center" data-testid="hero-search-loading">
              <div className="inline-flex items-center gap-2 text-sm text-[#0A1128]/60">
                <span className="inline-flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0A1128]/40 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0A1128]/40 animate-bounce" style={{ animationDelay: "120ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0A1128]/40 animate-bounce" style={{ animationDelay: "240ms" }} />
                </span>
                {t("hero_search.loading", "Diensten worden geladen…")}
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#0A1128]/55" data-testid="hero-search-empty">
              {query
                ? <>{t("hero_search.no_results", "Geen diensten gevonden voor")} <span className="font-semibold text-[#0A1128]">"{query}"</span></>
                : t("hero_search.unavailable", "Diensten zijn op dit moment niet beschikbaar. Probeer het zo opnieuw.")
              }
            </div>
          ) : (
            <div className="divide-y divide-[#EAE6DF]/60">
              {filtered.map((cat) => {
                const Icon = CATEGORY_ICONS[cat.icon] || Sparkles;
                return (
                  <div key={cat.id} className="py-2">
                    <button
                      type="button"
                      onClick={() => onPickCategory(cat.id)}
                      className="w-full flex items-center gap-3 px-5 py-2 hover:bg-[#F3F0EA]/50 transition-colors text-left"
                      data-testid={`hero-cat-${cat.id}`}
                    >
                      <div className="w-8 h-8 rounded-md bg-[#F3F0EA] text-[#0A1128] flex items-center justify-center flex-shrink-0">
                        <Icon size={15} strokeWidth={1.75} />
                      </div>
                      <span className="font-heading font-medium text-sm text-[#0A1128] flex-1">{catName(cat)}</span>
                      <span className="text-[11px] text-[#0A1128]/40 tabular">{cat.subcategories.length}</span>
                    </button>
                    <div>
                      {cat.subcategories.slice(0, query ? 99 : 4).map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => onPickDienst(s.id)}
                          className="w-full flex items-start gap-3 px-5 py-2 pl-16 hover:bg-[#F5A623]/8 transition-colors text-left group"
                          data-testid={`hero-dienst-${s.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <span className="text-[13px] font-medium text-[#0A1128] group-hover:text-[#F5A623] transition-colors">{subName(s)}</span>
                              <div className="flex gap-1">
                                {s.labels.map((l) => <Label key={l} label={l} />)}
                              </div>
                            </div>
                            <p className="text-[11px] text-[#0A1128]/50 mt-0.5 line-clamp-1">{subDiensten(s)}</p>
                          </div>
                        </button>
                      ))}
                      {!query && cat.subcategories.length > 4 && (
                        <button
                          type="button"
                          onClick={() => onPickCategory(cat.id)}
                          className="w-full text-left px-5 py-1.5 pl-16 text-[11px] font-semibold text-[#F5A623] hover:underline"
                          data-testid={`hero-more-${cat.id}`}
                        >
                          + {cat.subcategories.length - 4} {t("hero_search.more", "meer")}…
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
